import { motion } from "framer-motion";
import { PlayCircle, Users, LayoutGrid, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LangContext";

const icons = [PlayCircle, Users, LayoutGrid];

const Products = () => {
  const { t } = useLang();
  return (
    <section id="products" className="relative py-32 md:py-40 overflow-hidden">
      <div
        className="glow-orb"
        style={{ width: 600, height: 600, background: "rgba(4,120,87,0.20)", bottom: -100, right: -100 }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl"
        >
          <div className="text-[11px] tracking-[0.3em] uppercase text-po-accent mb-6">
            {t.products.eyebrow}
          </div>
          <h2 className="font-display text-white text-4xl sm:text-5xl md:text-6xl tracking-[-0.03em] font-semibold leading-[1.02]">
            {t.products.title}
            <br />
            <span className="font-serif-it text-po-text2">{t.products.titleItalic}</span>
          </h2>
          <p className="mt-8 max-w-2xl text-po-text2 text-base sm:text-lg leading-relaxed">{t.products.sub}</p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
          {t.products.cards.map((c, i) => {
            const Icon = icons[i] || PlayCircle;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8, delay: i * 0.1 }}
                data-testid={`product-card-${i}`}
                className="group relative rounded-3xl overflow-hidden border border-po-accent/15 bg-gradient-to-b from-po-accent/[0.08] via-po-surface/60 to-po-surface/40 p-8 md:p-10 min-h-[360px] flex flex-col justify-between hover:border-po-accent/40 transition-all duration-500"
              >
                <div className="absolute -top-32 -right-32 h-72 w-72 rounded-full bg-po-accent/20 blur-3xl opacity-50 group-hover:opacity-80 transition-opacity duration-700" />
                <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")",
                  }}
                />
                <div className="relative">
                  <div className="h-12 w-12 rounded-2xl bg-po-accent/15 border border-po-accent/30 flex items-center justify-center text-po-accent mb-8">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-white text-2xl md:text-[28px] tracking-tight font-medium leading-tight">{c.t}</h3>
                  <p className="mt-4 text-po-text2 text-[15px] leading-relaxed">{c.d}</p>
                </div>
                <div className="relative mt-8 flex items-center gap-2 text-po-accent text-[13px] tracking-wide font-medium opacity-70 group-hover:opacity-100 transition">
                  0{i + 1} / 03
                  <span className="ml-auto h-8 w-8 rounded-full border border-po-accent/30 flex items-center justify-center group-hover:bg-po-accent/10 transition">
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Products;
